import { Button, Input } from "@repo/ui";
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";

const navItems = [
  { to: "/", label: "หน้าแรก" },
  { to: "/daily", label: "ดวงวันนี้" },
  { to: "/compatibility", label: "เช็กความเข้ากันได้" },
  { to: "/news", label: "ข่าว" },
  { to: "/about", label: "เกี่ยวกับเรา" },
  { to: "/contact", label: "ติดต่อเรา" },
];

export function LuckyLayout() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [links, setLinks] = useState<{ line?: string; whatsapp?: string }>({});
  const location = useLocation();

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/api/settings");
        const data = (await res.json()) as { status: string; data: Record<string, string> };
        setLinks({ line: data.data.line_url, whatsapp: data.data.whatsapp_url });
      } catch {
        // ignore
      }
    })();
  }, []);

  async function subscribe(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      const res = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setStatus("success");
        setEmail("");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  }

  return (
    <div className="min-h-dvh bg-gradient-to-b from-[hsl(264_45%_6%)] via-[hsl(265_30%_10%)] to-[hsl(265_30%_14%)] text-foreground">
      <header className="sticky top-0 z-40 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border/60">
        <div className="mx-auto max-w-6xl px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="size-7 rounded-md bg-gradient-to-br from-indigo-400 to-violet-500 grid place-items-center text-[10px] font-bold text-black/80">
              LN
            </div>
            <span className="font-display text-lg tracking-wide">Lucky News</span>
          </Link>
          <nav className="hidden md:flex gap-6 text-sm">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `hover:text-primary transition-colors ${isActive ? "text-primary" : "text-foreground/80"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <Button asChild>
              <Link to="/daily">อ่านดวงวันนี้</Link>
            </Button>
            <a
              href={links.line || "https://line.me/R/ti/p/@luckynews"}
              className="text-sm text-foreground/70 hover:text-primary"
            >
              LINE
            </a>
            <a
              href={links.whatsapp || "https://wa.me/0000000000"}
              className="text-sm text-foreground/70 hover:text-primary"
            >
              WhatsApp
            </a>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-10 md:py-14">
        <Outlet key={location.pathname} />
      </main>
      <footer className="mt-10 border-t border-border/60">
        <div className="mx-auto max-w-6xl px-4 py-10 grid gap-8 md:grid-cols-3">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="size-7 rounded-md bg-gradient-to-br from-indigo-400 to-violet-500 grid place-items-center text-[10px] font-bold text-black/80">
                LN
              </div>
              <span className="font-display text-lg">Lucky News</span>
            </div>
            <p className="text-sm text-foreground/70">
              บล็อกดูดวงภาษาไทย อ่านดวงรายวัน เช็กความเข้ากันได้ และบทความเสริมดวง
            </p>
            <div className="mt-4 flex gap-3">
              <a
                className="text-sm hover:text-primary"
                href={links.line || "https://line.me/R/ti/p/@luckynews"}
              >
                LINE
              </a>
              <a
                className="text-sm hover:text-primary"
                href={links.whatsapp || "https://wa.me/0000000000"}
              >
                WhatsApp
              </a>
              <a
                className="text-sm hover:text-primary"
                href="#"
                onClick={(e) => e.preventDefault()}
              >
                สนับสนุนเรา
              </a>
            </div>
          </div>
          <div>
            <h4 className="font-display mb-3">เมนู</h4>
            <div className="grid gap-2 text-sm">
              {navItems.map((n) => (
                <Link key={n.to} to={n.to} className="text-foreground/80 hover:text-primary">
                  {n.label}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-display mb-3">สมัครจดหมายข่าว</h4>
            <p className="text-sm text-foreground/70 mb-3">รับบทความดีๆ และดวงรายสัปดาห์ทางอีเมล</p>
            <form onSubmit={subscribe} className="flex gap-2">
              <Input
                type="email"
                required
                placeholder="อีเมลของคุณ"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button type="submit" disabled={status === "loading"}>
                {status === "loading" ? "กำลังสมัคร..." : "สมัคร"}
              </Button>
            </form>
            {status === "success" && (
              <p className="text-xs mt-2 text-green-400">สมัครสำเร็จ ขอบคุณค่ะ/ครับ</p>
            )}
            {status === "error" && (
              <p className="text-xs mt-2 text-red-400">เกิดข้อผิดพลาด โปรดลองใหม่</p>
            )}
          </div>
        </div>
        <div className="border-t border-border/60 py-6 text-center text-xs text-foreground/60">
          © {new Date().getFullYear()} Lucky News · luckynews.co
        </div>
      </footer>
    </div>
  );
}

export default LuckyLayout;
